import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Mail, Plug, KeyRound, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const GmailBar = ({ status, refresh }) => {
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const connected = status?.connected;
  const own = status?.own_sending;

  const connectOauth = async () => {
    try {
      const { url } = await api.gmailLogin();
      window.location.href = url;
    } catch (e) {
      toast.error("Gmail OAuth ikke konfigurert.");
    }
  };

  const connectSmtp = async () => {
    if (own && !email.trim()) { toast.error("Skriv inn Gmail-adressen"); return; }
    if (!pw.trim()) { toast.error("Lim inn app-passordet"); return; }
    setBusy(true);
    try {
      await api.connectSmtp(pw, own ? email.trim() : undefined);
      toast.success("Gmail tilkoblet med app-passord!");
      setPwOpen(false);
      setPw("");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke koble til");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await api.gmailDisconnect();
    toast.success("Frakoblet");
    refresh?.();
  };

  return (
    <div
      data-testid="gmail-status-indicator"
      className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2"
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent">
        <Mail className="h-4 w-4 text-accent-foreground" />
      </div>
      <div className="leading-tight">
        <div className="text-xs font-mono-brand uppercase tracking-wider text-muted-foreground">Avsender:</div>
        <div className="flex items-center gap-2">
          <span data-testid="sender-email-address" className="text-sm font-semibold text-foreground">
            {status?.email}
          </span>
          <span className="flex items-center gap-1 text-xs font-medium">
            <span className={`h-2 w-2 rounded-full ${connected ? "bg-[#16A34A]" : "bg-[#D97706]"}`} />
            {connected ? `Tilkoblet${status?.method === "smtp" ? " (app-passord)" : ""}` : own ? "Ikke tilkoblet" : "Simulert modus"}
          </span>
        </div>
      </div>
      {connected ? (
        <Button size="sm" variant="ghost" onClick={disconnect} className="ml-auto text-xs" data-testid="gmail-disconnect-button">
          Koble fra
        </Button>
      ) : (
        <div className="ml-auto flex items-center gap-1.5">
          {!own && (
            <Button size="sm" variant="outline" onClick={connectOauth} className="gap-1.5" data-testid="gmail-connect-button">
              <Plug className="h-4 w-4" /> Google-innlogging
            </Button>
          )}
          <Button size="sm" onClick={() => setPwOpen(true)} className="gap-1.5" data-testid="gmail-apppassword-button">
            <KeyRound className="h-4 w-4" /> {own ? "Koble til Gmail" : "App-passord"}
          </Button>
        </div>
      )}

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="bg-card" data-testid="gmail-apppassword-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> Koble til med app-passord
            </DialogTitle>
            <DialogDescription className="text-left space-y-2 pt-1">
              Send godkjente e-poster direkte fra {own ? "bedriftens egen Gmail" : <b>{status?.email}</b>}. Passordet lagres kryptert:
              <br />
              1. Slå på <b>2-trinns bekreftelse</b> på Google-kontoen din.
              <br />
              2. Lag et app-passord (16 tegn) her:
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline ml-1">
                myaccount.google.com/apppasswords <ExternalLink className="h-3 w-3" />
              </a>
              <br />
              3. Lim inn passordet under.
            </DialogDescription>
          </DialogHeader>
          {own && (
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="bedrift@gmail.com"
              data-testid="gmail-email-input"
              className="bg-background"
            />
          )}
          <Input
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="abcd efgh ijkl mnop"
            data-testid="gmail-apppassword-input"
            className="bg-background font-mono"
            onKeyDown={(e) => e.key === "Enter" && connectSmtp()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Avbryt</Button>
            <Button onClick={connectSmtp} disabled={busy} className="gap-1.5" data-testid="gmail-apppassword-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Koble til
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
